import { useCallback } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useEligibleLeadShifts } from 'queries/shift'
import { Box, Flex, Heading, Loading, Message, Text } from '@workwhile/ui'
import { SelectableShiftCard } from '../../SelectableShiftCard'
import { ShiftSummary } from '../../ShiftSummary'
import { ShiftWorkerAvatars } from '../../ShiftWorkerAvatars'
import { LinkLeadShiftsFormValues } from '../types'

interface Props {
  companyId: number
  shiftId: number
  timezone: string
}

export const SelectLeadShiftsStep = ({
  companyId,
  shiftId,
  timezone,
}: Props) => {
  const { control } = useFormContext<LinkLeadShiftsFormValues>()
  const {
    field: { value: selectedShiftIds, onChange },
  } = useController({ name: 'leadShiftIds', control })

  const handleToggle = useCallback(
    (leadShiftId: number) => {
      onChange(
        selectedShiftIds.includes(leadShiftId)
          ? selectedShiftIds.filter((id) => id !== leadShiftId)
          : [...selectedShiftIds, leadShiftId]
      )
    },
    [selectedShiftIds, onChange]
  )

  const {
    data: eligibleLeadShifts,
    isLoading,
    isError,
  } = useEligibleLeadShifts({ companyId, shiftId }, { refetchOnMount: false })

  const renderContent = () => {
    if (isLoading) return <Loading />

    if (isError) {
      return (
        <Message variant="error">
          Failed to load lead shifts. Please try again.
        </Message>
      )
    }

    if (!eligibleLeadShifts?.length) {
      return <Text>No shifts available to link</Text>
    }

    return (
      <Flex flexDirection="column" gap={3}>
        {eligibleLeadShifts.map((leadShift) => (
          <SelectableShiftCard
            key={leadShift.id}
            id={leadShift.id}
            isSelected={selectedShiftIds.includes(leadShift.id)}
            onToggle={handleToggle}
          >
            <ShiftSummary
              positionName={leadShift.position.name}
              startsAt={leadShift.startsAt}
              endsAt={leadShift.endsAt}
              timezone={leadShift.location?.timezone || timezone}
              locationName={leadShift.location?.name}
            />
            <Box mt={2}>
              <ShiftWorkerAvatars shift={leadShift} />
            </Box>
          </SelectableShiftCard>
        ))}
      </Flex>
    )
  }

  return (
    <Box>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={4}
      >
        <Heading level={3} my={0}>
          Select shifts to link
        </Heading>
        <Text>Showing overlapping lead shifts at the same location</Text>
      </Flex>
      {renderContent()}
    </Box>
  )
}
